import type { RequestHeaders, RequestParams } from '@bv/api/base';
import { WithPagination } from '@bv/api/crud';

import type { FieldOption, Options } from '../types/workflow';

class CustomAttributesApi extends WithPagination {
  constructor() {
    super('contacts/custom-attributes?limit=1000');
  }
}
const customAttributesApi = new CustomAttributesApi();

// Cache the latest contact field options to allow onChange to look up metadata
const contactFieldOptionsCache: Record<
  string,
  { label: string; value: string; type?: string; list_values?: string[] }
> = {};

const staticFields: Options = [
  { label: 'Name', value: 'full_name' },
  { label: 'Phone Number', value: 'phone_number' },
  { label: 'Email Address', value: 'email' },
  { label: 'Company', value: 'company_name' },
  { label: 'Country', value: 'country_name' },
  { label: 'City', value: 'city_name' },
  { label: 'bio', value: 'bio' },
];

// initialize cache with static fields immediately
staticFields.forEach((opt) => {
  contactFieldOptionsCache[opt.value as string] = opt as any;
});

// Create fetcher for contact fields options with pagination support
const fetchContactFieldsOptions = async (config?: {
  params?: RequestParams;
  headers?: RequestHeaders;
}): Promise<{ data: FieldOption[]; meta: any }> => {
  const { data, meta } = await customAttributesApi.paginated(config);
  const dynamicFields = (
    data as Array<{ display_name: string; name: string; key: string; type?: string; list_values?: string[] }>
  ).map((attribute) => ({
    label: attribute.display_name,
    value: attribute.key,
    type: attribute.type,
    list_values: attribute.list_values,
  }));

  // only include static fields on the first page or if page is not specified
  const page = config?.params?.page ? Number(config.params.page) : 1;
  const searchTerm = (((config?.params?.q || config?.params?.['filter[q]']) as string) || '').toLowerCase();

  let filteredStaticFields = staticFields as FieldOption[];
  if (searchTerm) {
    filteredStaticFields = filteredStaticFields.filter(
      (opt) => opt.label.toLowerCase().includes(searchTerm) || String(opt.value).toLowerCase().includes(searchTerm),
    );
  }

  const optionsData: FieldOption[] = page === 1 ? [...filteredStaticFields, ...dynamicFields] : dynamicFields;

  // Update cache for onChange lookup by selected value (display_name)
  optionsData.forEach((opt) => {
    contactFieldOptionsCache[opt.value as string] = opt as any;
  });

  return {
    meta,
    data: optionsData,
  };
};

const contactFieldsOptions = () => {
  return fetchContactFieldsOptions;
};

export { contactFieldsOptions, staticFields, contactFieldOptionsCache };
